import { useState } from 'react';
import useNotification from './useNotification';
import useDocuments from './useDocuments';
import mermaid from 'mermaid';
import { copyWithHtml, stripMarkdown } from '../utils/clipboardUtils';

/**
 * Custom hook for common content actions (export, copy, refresh, save)
 * Shared across all dynamic split view components
 */
const useContentActions = (contentRef, contentType, projectId, conversationId) => {
  const [isExporting, setIsExporting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { showNotification } = useNotification();
  const { createDocument } = useDocuments();

  // Handler for exporting content to markdown file
  const handleExport = () => {
    try {
      setIsExporting(true);
      const content = contentRef.current;

      if (!content) {
        showNotification({
          message: `No ${contentType} content to export`,
          type: "warning"
        });
        setIsExporting(false);
        return;
      }

      const date = new Date().toISOString().split('T')[0];
      const filename = `${contentType.toLowerCase().replace(/\s+/g, '-')}-${date}.md`;
      const blob = new Blob([content], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setIsExporting(false);
        showNotification({
          message: `${contentType} document exported successfully`,
          type: "success"
        });
      }, 100);
    } catch (error) {
      console.error(`Error exporting ${contentType} document:`, error);
      showNotification({
        message: `Failed to export ${contentType} document`,
        type: "error"
      });
      setIsExporting(false);
    }
  };

  // Handler for copying content to clipboard with HTML table support
  const handleCopy = async () => {
    try {
      const content = contentRef.current;
      if (!content) {
        showNotification({
          message: `No ${contentType} content to copy`,
          type: "warning"
        });
        return;
      }

      const plainText = stripMarkdown(content);
      await copyWithHtml(content, plainText);
      showNotification({
        message: `${contentType} content copied to clipboard`,
        type: "success"
      });
    } catch (error) {
      console.error(`Error copying ${contentType} content:`, error);
      showNotification({
        message: `Failed to copy ${contentType} content to clipboard`,
        type: "error"
      });
    }
  };

  // Handler for refreshing diagrams
  const handleRefreshDiagrams = () => {
    try {
      // Re-render all mermaid diagrams
      mermaid.run();
      showNotification({
        message: `${contentType} diagrams refreshed`,
        type: "info"
      });
    } catch (error) {
      console.error("Error refreshing diagrams:", error);
      showNotification({
        message: "Failed to refresh diagrams",
        type: "error"
      });
    }
  };

  // Handler for saving content as document
  const handleSaveToDocument = async (documentType) => {
    if (!projectId) {
      showNotification({
        message: `Project ID is required to save ${contentType} document`,
        type: "error"
      });
      return;
    }

    try {
      setIsSaving(true);

      // Get the current content
      const content = contentRef.current;

      if (!content) {
        showNotification({
          message: `No ${contentType} content to save`,
          type: "warning"
        });
        setIsSaving(false);
        return;
      }

      // Create a document with the content
      const document = await createDocument(projectId, {
        title: contentType,
        content: content,
        type: documentType || contentType.toUpperCase().replace(/\s+/g, '_')
      });

      showNotification({
        message: `${contentType} document saved successfully`,
        type: "success"
      });

      return document;
    } catch (error) {
      console.error(`Error saving ${contentType} to document:`, error);
      showNotification({
        message: `Failed to save ${contentType}: ${error.message}`,
        type: "error"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const getExportContent = () => {
    const content = contentRef.current;
    if (!content) return null;
    const date = new Date().toISOString().split('T')[0];
    const filename = `${contentType.toLowerCase().replace(/\s+/g, '-')}-${date}`;
    return { content, filename, conversationId, projectId };
  };

  return {
    isExporting,
    isSaving,
    handleExport,
    handleCopy,
    handleRefreshDiagrams,
    handleSaveToDocument,
    getExportContent
  };
};

export default useContentActions;